import React, { FormEvent, useCallback, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createDocument, scanDocument, updateDocument } from '../../services/mutations';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { FileIcon } from '../../components/attachments/FileIcon';
import { formatFileSize } from '../../components/attachments/fileIconUtils';
import {
  CheckCircleIcon,
  DocumentTextIcon,
  RefreshIcon,
  SaveIcon,
  SpinnerIcon,
  TrashIcon,
  UploadCloudIcon,
  WizardIcon,
  XIcon,
} from '../../components/ui/Icons';
import { Document } from './useData';

const ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv';
const MAX_BYTES = 15 * 1024 * 1024;
const SCAN_IMAGE_HINT = 'AI scan works with PDF and image files (PNG, JPEG, WebP, GIF, BMP).';

function titleFromFilename(name: string): string {
  return name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
}

function errMsg(e: unknown): string {
  const msg = (e as any)?.response?.data?.message || (e as any)?.message || 'Something went wrong';
  return Array.isArray(msg) ? msg.join(', ') : String(msg);
}

function titleFromScanPayload(payload: Record<string, unknown> | null | undefined): string | null {
  if (!payload) return null;
  const supplier = typeof payload.supplierName === 'string' ? payload.supplierName.trim() : '';
  const invoice = typeof payload.invoiceNumber === 'string' ? payload.invoiceNumber.trim() : '';
  const parts = [supplier, invoice].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

function isScannableFile(file: File | null): boolean {
  if (!file) return false;
  const mime = (file.type || '').toLowerCase().split(';')[0].trim();
  if (mime.startsWith('image/')) return true;
  if (mime === 'application/pdf' || mime === 'application/x-pdf') return true;
  return /\.pdf$/i.test(file.name);
}

const StepStrip: React.FC<{
  fileReady: boolean;
  busy: boolean;
}> = ({ fileReady, busy }) => {
  const { colors } = useTheme();
  const steps = [
    { key: 'upload', label: 'Upload', done: fileReady, active: !fileReady },
    { key: 'scan', label: 'Scan', done: busy, active: fileReady },
  ] as const;

  return (
    <ol className="flex items-center gap-1 sm:gap-2">
      {steps.map((step, index) => (
        <li key={step.key} className="flex min-w-0 items-center gap-1 sm:gap-2">
          {index > 0 && (
            <span
              className={cn(
                'hidden h-px w-4 shrink-0 sm:block sm:w-8',
                step.done || step.active ? colors.primary : colors.surfaceHover,
              )}
              aria-hidden
            />
          )}
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide transition sm:px-3',
              step.done
                ? cn(colors.primary, colors.primaryText)
                : step.active
                  ? cn(colors.info, 'ring-1', colors.primaryBorderAll)
                  : cn(colors.surfaceHover, colors.textMuted),
            )}
          >
            <span
              className={cn(
                'flex h-4 w-4 items-center justify-center rounded-full text-[10px]',
                step.done
                  ? 'bg-white/20'
                  : step.active
                    ? cn(colors.primary, colors.primaryText)
                    : cn(colors.surfaceHover, colors.textMuted),
              )}
            >
              {step.done ? <CheckCircleIcon className="h-3 w-3" /> : index + 1}
            </span>
            <span className="truncate">{step.label}</span>
          </span>
        </li>
      ))}
    </ol>
  );
};

export const New: React.FC = () => {
  const navigate = useNavigate();
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const inputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyMode, setBusyMode] = useState<'create' | 'scan' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fileReady = Boolean(file);
  const canScan = isScannableFile(file);

  const clearFile = useCallback(() => {
    setFile(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const applyFile = useCallback(
    (next: File | null) => {
      if (!next) {
        clearFile();
        return;
      }
      if (next.size > MAX_BYTES) {
        setError('File exceeds the 15 MB limit.');
        return;
      }
      setError(null);
      setFile(next);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return next.type.startsWith('image/') ? URL.createObjectURL(next) : null;
      });
    },
    [clearFile],
  );

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) setDragActive(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragActive(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) applyFile(dropped);
  };

  const buildFormData = (resolvedTitle: string) => {
    if (!file) throw new Error('Add a document to continue.');
    const data = new FormData();
    data.append('title', resolvedTitle);
    data.append('file', file);
    return data;
  };

  const createDraft = async (): Promise<Document> => {
    const resolvedTitle = file ? titleFromFilename(file.name) || 'Untitled document' : 'Untitled document';
    return createDocument(buildFormData(resolvedTitle));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!file) {
      setError('Add a document to continue.');
      return;
    }
    setBusy(true);
    setBusyMode('create');
    setError(null);
    try {
      const document = await createDraft();
      navigate(`/documents/${document.id}`);
    } catch (e) {
      setError(errMsg(e) || 'Failed to create document');
    } finally {
      setBusy(false);
      setBusyMode(null);
    }
  };

  const createAndScan = async () => {
    if (!file) {
      setError('Add a document to continue.');
      return;
    }
    if (!canScan) {
      setError(`${SCAN_IMAGE_HINT} Create a draft without scanning, or upload a PDF/image.`);
      return;
    }
    setBusy(true);
    setBusyMode('scan');
    setError(null);
    let documentId: number | null = null;
    const filenameTitle = titleFromFilename(file.name);
    try {
      const document = await createDraft();
      documentId = document.id;
      const scanned = await scanDocument(document.id);
      const suggested = titleFromScanPayload(scanned?.payload as Record<string, unknown> | undefined);
      if (suggested && (!filenameTitle || document.title === filenameTitle)) {
        try {
          await updateDocument(document.id, { title: suggested });
        } catch {
          // non-fatal — payload still has extracted fields
        }
      }
      navigate(`/documents/${document.id}`, { state: { justScanned: true } });
    } catch (e) {
      const message = errMsg(e);
      if (documentId) {
        navigate(`/documents/${documentId}`, { state: { scanError: message } });
        return;
      }
      setError(message || 'Failed to scan document');
    } finally {
      setBusy(false);
      setBusyMode(null);
    }
  };

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-6">
      <div className="mb-5 flex flex-col gap-4 sm:mb-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <Link to="/documents" className={cn('text-sm font-medium hover:underline', colors.textMuted)}>
            ← Documents
          </Link>
          <h1 className={cn('mt-2 text-2xl font-semibold tracking-tight', colors.text)}>New document</h1>
          <p className={cn('mt-1 max-w-xl text-sm', colors.textMuted)}>
            Attach an invoice, then scan with AI. Title, type, and department are set on the document page.
          </p>
        </div>
        <StepStrip fileReady={fileReady} busy={busy} />
      </div>

      <form onSubmit={submit} className={cn('overflow-hidden rounded-2xl border shadow-sm', colors.surface, colors.border)}>
        {error && (
          <div className={cn('border-b px-4 py-3 text-sm md:px-6', colors.border, colors.error)}>
            {error}
          </div>
        )}

        <section
          className="p-5 md:p-6"
          onDragOver={onDragOver}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className={cn('text-sm font-semibold', colors.text)}>Document</h2>
              <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                PDF and images for AI scan · Office/CSV also accepted · max 15 MB
              </p>
            </div>
            {fileReady && (
              <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide', colors.success)}>
                <CheckCircleIcon className="h-3 w-3" />
                Attached
              </span>
            )}
          </div>

          {!file ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className={cn(
                'flex min-h-[280px] w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-16 text-center transition md:min-h-[340px]',
                colors.border,
                dragActive
                  ? cn('scale-[1.01] shadow-sm', colors.primaryBorderAll, colors.info)
                  : cn(colors.interactiveHover),
              )}
            >
              <div
                className={cn(
                  'mb-4 flex h-16 w-16 items-center justify-center rounded-2xl transition',
                  dragActive ? colors.info : cn(colors.surfaceHover, colors.textMuted),
                )}
              >
                <UploadCloudIcon className="h-8 w-8" />
              </div>
              <p className={cn('text-base font-semibold', colors.text)}>
                {dragActive ? 'Release to attach' : 'Drop your document here'}
              </p>
              <p className={cn('mt-1.5 text-sm', colors.textMuted)}>or click to browse from your computer</p>
              <span
                className={cn(
                  'mt-5 inline-flex items-center rounded-xl border px-3.5 py-2 text-xs font-medium',
                  colors.border,
                  colors.text,
                )}
              >
                Choose file
              </span>
            </button>
          ) : (
            <div
              className={cn(
                'flex min-h-[280px] flex-col overflow-hidden rounded-2xl border md:min-h-[340px]',
                colors.border,
                dragActive && 'ring-2 ring-indigo-400/60',
              )}
            >
              <div
                className={cn(
                  'relative flex flex-1 items-center justify-center',
                  colors.headerBg,
                  previewUrl ? 'p-0' : 'p-8',
                )}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="" className="max-h-[260px] w-full object-contain md:max-h-[300px]" />
                ) : (
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className={cn('flex h-24 w-24 items-center justify-center rounded-2xl border shadow-sm', colors.border, colors.surface)}>
                      <FileIcon fileName={file.name} size="md" />
                    </div>
                    <p className={cn('text-xs', colors.textMuted)}>Preview not available for this file type</p>
                  </div>
                )}
              </div>
              <div className={cn('flex flex-wrap items-center gap-3 border-t px-4 py-3', colors.border, colors.surface)}>
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className={cn('hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl border sm:flex', colors.border)}>
                    <DocumentTextIcon className={cn('h-5 w-5', colors.primaryFg)} />
                  </div>
                  <div className="min-w-0">
                    <p className={cn('truncate text-sm font-medium', colors.text)}>{file.name}</p>
                    <p className={cn('text-xs', colors.textMuted)}>
                      {formatFileSize(file.size)}
                      {!canScan && ' · AI scan needs a PDF or image'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    title="Replace file"
                    aria-label="Replace file"
                    className={cn(
                      'inline-flex h-9 w-9 items-center justify-center rounded-xl border transition disabled:opacity-50',
                      colors.border,
                      colors.textMuted,
                      colors.interactiveHover,
                    )}
                  >
                    <RefreshIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={clearFile}
                    disabled={busy}
                    title="Remove file"
                    aria-label="Remove file"
                    className={cn(
                      'inline-flex h-9 w-9 items-center justify-center rounded-xl border transition disabled:opacity-50',
                      colors.border,
                      isDark ? 'text-red-400' : 'text-red-600',
                      colors.interactiveHover,
                    )}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              applyFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />

          {fileReady && (
            <p className={cn('mt-4 rounded-xl border border-dashed px-3.5 py-3 text-xs leading-relaxed', colors.border, colors.textMuted)}>
              {canScan
                ? 'Scan with AI creates a draft, extracts invoice fields, and opens the document for review. Set title, type, and department there.'
                : `${SCAN_IMAGE_HINT} You can still create a draft without scanning.`}
            </p>
          )}
        </section>

        <div
          className={cn(
            'sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 backdrop-blur md:px-6',
            colors.headerBg,
            colors.border,
          )}
        >
          <p className={cn('text-xs', colors.textMuted)}>
            {!fileReady
              ? 'Step 1 of 2 — attach a document'
              : canScan
                ? 'Step 2 of 2 — scan with AI or create a draft'
                : 'Step 2 of 2 — create a draft (PDF or image needed for AI scan)'}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => navigate('/documents')}
              disabled={busy}
              title="Cancel"
              aria-label="Cancel"
              className={cn(
                'inline-flex h-10 w-10 items-center justify-center rounded-xl border transition disabled:opacity-50',
                colors.border,
                colors.textMuted,
                colors.interactiveHover,
              )}
            >
              <XIcon className="h-4 w-4" />
            </button>
            <button
              type="submit"
              disabled={busy || !file}
              title={busyMode === 'create' ? 'Creating draft…' : 'Create draft'}
              aria-label={busyMode === 'create' ? 'Creating draft' : 'Create draft'}
              className={cn(
                'inline-flex h-10 w-10 items-center justify-center rounded-xl border transition disabled:opacity-50',
                colors.border,
                colors.textMuted,
                colors.interactiveHover,
              )}
            >
              {busyMode === 'create' ? <SpinnerIcon className="h-4 w-4" /> : <SaveIcon className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={createAndScan}
              disabled={busy || !file || !canScan}
              title={!canScan ? SCAN_IMAGE_HINT : busyMode === 'scan' ? 'Scanning…' : 'Scan with AI'}
              aria-label={busyMode === 'scan' ? 'Scanning with AI' : 'Scan with AI'}
              className={cn(
                'inline-flex h-10 w-10 items-center justify-center rounded-xl text-white transition disabled:opacity-50',
                colors.primary,
              )}
            >
              {busyMode === 'scan' ? <SpinnerIcon className="h-4 w-4" /> : <WizardIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
